import {useEffect, useState, type FormEvent} from "react";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import {useTranslation} from "react-i18next";
import {FloatingLabel} from "react-bootstrap";
import {countries} from "../../../util/CountryCodes";
import {useUnlinkedUsers} from "../../../hooks/queries/useUsers";
import type {Player} from "../../../types/domain";

// The /players/:id detail response lacks `ladders`, but the form only edits the fields it
// cares about, so accept a partial input and submit a partial Player.
type EditablePlayer = Pick<Player, "id" | "firstname" | "lastname" | "nickname" | "nationality" | "userId"> & Partial<Player>;

interface UpdatePlayerModalProps {
    isOpen: boolean;
    closeModal: () => void;
    handleSubmit: (player: EditablePlayer) => void;
    player: EditablePlayer;
}

function UpdatePlayerModal(props: UpdatePlayerModalProps) {
    const { t, i18n } = useTranslation();
    const [ player, setPlayer ] = useState<EditablePlayer>(props.player)
    const [ submitButtonDisabled, setSubmitButtonDisabled ] = useState(false)
    const [validated, setValidated] = useState(false);
    const { data: unlinkedPlayers = [] } = useUnlinkedUsers(player.userId, { enabled: props.isOpen });

    useEffect(() => {
        if (props.isOpen === false) {
            setValidated(false)
            setPlayer(props.player)
            setSubmitButtonDisabled(false)
        }
    }, [props.player, props.isOpen]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.preventDefault();
            event.stopPropagation();
        } else {
            event.preventDefault();
            event.stopPropagation();
            setSubmitButtonDisabled(true)
            props.handleSubmit(player)
        }

        setValidated(true);
    };

    return (
      <Modal
        show={props.isOpen}
        onHide={props.closeModal}
      >
          <Modal.Header closeButton>
              <Modal.Title>{t("editPlayer")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Form.Group >
                      <FloatingLabel
                        controlId="floatingFirstname"
                        label={t("firstName")}
                        className="mb-3"
                      >
                          <Form.Control required type="text" onChange={e => setPlayer({...player, firstname: e.target.value})} value={player.firstname} placeholder=""/>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group>
                      <FloatingLabel
                        controlId="floatingLastname"
                        label={t("lastName")}
                        className="mb-3"
                      >
                          <Form.Control required type="text" onChange={e => setPlayer({...player, lastname: e.target.value})} value={player.lastname} placeholder=""/>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group>
                      <FloatingLabel
                        controlId="floatingNickname"
                        label={t("nickname") + " (" + t("optional") + ")"}
                        className="mb-3"
                      >
                          <Form.Control type="text" onChange={e => setPlayer({...player, nickname: e.target.value})} value={player.nickname ?? ""} placeholder=""/>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group className="mb-3">
                      <FloatingLabel
                        controlId="floatingNationality"
                        label={t("nationality")}
                        className="mb-3"
                      >
                          <Form.Select onChange={e => setPlayer({...player, nationality: e.target.value})} value={player.nationality}>
                              {countries.map((country) => {
                                  return (<option key={"country-"+country.code} value={country.code}>{i18n.language === "cs" ? country.czech : country.english}</option>)
                              })}
                          </Form.Select>
                      </FloatingLabel>
                  </Form.Group>
                  <Form.Group className="mb-3">
                      <FloatingLabel
                        controlId="userAccount"
                        label={t("userAccount")}
                        className="mb-3"
                      >
                          <Form.Select onChange={e => setPlayer({...player, userId: e.target.value ? Number(e.target.value) : null})} value={player.userId ?? ""}>
                              {unlinkedPlayers.map((user) => {
                                  return (<option key={"user-"+user.id} value={user.id ?? ""}>{user.id ? user.username : t("noUserAccount")}</option>)
                              })}
                          </Form.Select>
                      </FloatingLabel>
                  </Form.Group>
                  <div className="d-flex">
                      <Button type="submit" disabled={submitButtonDisabled} style={{flex: "1"}}>{t("save")}</Button>
                  </div>
              </Form>
          </Modal.Body>
      </Modal>
    );
}

export default UpdatePlayerModal
